import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import stripeService from '../../services/stripeService';

const SubscriptionSuccess = () => {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Helper function to format dates
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };
  
  // Format currency for display
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return 'Free';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  useEffect(() => {
    const verifySubscription = async () => {
      try {
        setLoading(true);
        
        // Extract session ID from URL query parameters
        const params = new URLSearchParams(location.search);
        const sessionId = params.get('session_id');
        
        if (!sessionId) {
          setError('No session ID provided. Unable to verify subscription.');
          setLoading(false);
          return;
        }
        
        console.log('Verifying subscription with session ID:', sessionId);
        
        // Get subscription details based on current user role
        // For admin user, use the admin endpoint
        let response;
        try {
          // Attempt to get subscription via admin endpoint
          response = await stripeService.getTenantSubscription();
        } catch (err) {
          console.error('Error fetching subscription via admin endpoint:', err);
          // If that fails, try tenant endpoint
          response = await stripeService.getSubscriptionDetails(sessionId);
        }
        
        if (response && (response.success || response.data)) {
          const subData = response.data || response;
          console.log('Subscription verified:', subData);
          setSubscription(subData);
        } else {
          throw new Error(response?.message || 'Failed to verify subscription');
        }
      } catch (err) {
        console.error('Error verifying subscription:', err);
        setError(`Error: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    verifySubscription();
  }, [location]);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh'
      }}>
        <CircularProgress size={60} />
        <Typography sx={{ mt: 2 }}>
          Verifying your subscription...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      maxWidth: 800,
      mx: 'auto',
      p: 3
    }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4,
          borderRadius: 2,
          textAlign: 'center'
        }}
      >
        {error ? (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
            <Typography variant="h5" gutterBottom>
              We couldn't verify your subscription
            </Typography>
            <Typography variant="body1" sx={{ mb: 4 }}>
              There was an error validating your subscription. Please contact support if you believe this is an error.
            </Typography>
            <Button 
              variant="contained"
              onClick={() => navigate('/subscription')}
            >
              Return to Plans
            </Button>
          </>
        ) : (
          <>
            <Box sx={{ mb: 4 }}>
              <CheckCircleOutlineIcon 
                color="success" 
                sx={{ fontSize: 80 }} 
              />
              
              <Typography variant="h4" sx={{ mt: 2, fontWeight: 'bold' }}>
                Subscription Successful!
              </Typography>
              
              <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
                Thank you for your subscription. Your payment has been processed successfully.
              </Typography>
            </Box>
            
            {subscription && (
              <Box sx={{ textAlign: 'left', mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Subscription Details:
                </Typography>
                
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    bgcolor: 'background.default',
                    borderRadius: 2
                  }}
                >
                  <List disablePadding>
                    <ListItem>
                      <ListItemIcon>
                        <ReceiptIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Plan" 
                        secondary={subscription.planName || 'Standard Plan'}
                      />
                    </ListItem>
                    
                    <Divider component="li" />
                    
                    <ListItem>
                      <ListItemIcon>
                        <PaymentIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Price" 
                        secondary={`${formatCurrency(subscription.amount)} / ${subscription.interval || 'month'}`}
                      />
                    </ListItem>
                    
                    <Divider component="li" />
                    
                    <ListItem>
                      <ListItemIcon>
                        <CalendarTodayIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Next Billing Date" 
                        secondary={formatDate(subscription.currentPeriodEnd)}
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Box>
            )}
            
            <Box sx={{ mt: 4 }}>
              <Button 
                variant="contained" 
                size="large"
                onClick={() => navigate('/dashboard')}
              >
                Continue to Dashboard
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default SubscriptionSuccess;